import { Maybe, fromNullable, just, nothing } from 'maybeasy';
import { observer } from 'mobx-react';
import type { CSSProperties } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
// Import from the package root — the subpath entry points
// ('scorm-again/scorm12', 'scorm-again/cross-frame-lms') declare a
// default export in their .d.ts but the ESM build ships them only as
// named exports, which trips the bundler at build time. The root
// index re-exports both as consistent named exports.
import { CrossFrameLMS, Scorm12API } from 'scorm-again';
import { callApi, postToApi } from '../../../Appy';
import { Link } from '../../../Resource/Types';
import SegmentStore from '../../../SegmentStore';
import { segmentResourceDecoder } from '../../../SegmentStore/Decoders';
import {
  ScormSegment as ScormSegmentType,
  SegmentResource,
  SegmentState,
} from '../../../SegmentStore/Types';
import { T } from '../../../Translations';
import * as style from './style.module.css';

// Iframe height floor. Mirrors the CSS +min-height+ in
// style.module.css so a tiny +scrollHeight+ report (a mostly-empty
// SCORM test with 50px of content, a scenario mid-transition) can't
// collapse the iframe below the CSS default once the inline
// +style.height+ starts overriding it.
//
// NO ceiling: the whole point of this ticket is that only the outer
// platform page scrolls. Capping the iframe height creates the exact
// double-scroll problem the fix is meant to eliminate (iframe hits
// cap, its inner document scrolls, learner sees two scrollbars). We
// let the iframe grow to whatever the content reports; the outer
// page's scroll handles overflow. A 10000px content report becomes a
// 10000px iframe and a 10000px+chrome outer page -- perfectly fine.
const MIN_IFRAME_HEIGHT_PX = 400;

// Packages read this as --exo-visible-height via the injected shim (DVLP-8187).
const visibleSegmentHeight = (): number => {
  const navHeight = fromNullable(document.querySelector<HTMLElement>('[data-segment-sticky-nav]'))
    .map((nav) => nav.offsetHeight)
    .getOrElseValue(0);
  return Math.max(MIN_IFRAME_HEIGHT_PX, window.innerHeight - navHeight);
};

// Type guard for the child-side shim's resize postMessage. See
// +ProcessScormPackageJob.build_shim_script_block+'s height-reporter
// block for the sender. Keeping validation in a named guard makes the
// message-handler +useEffect+ read as a single branch and lets a spec
// exercise the shape check in isolation from React lifecycle.
interface ScormResizeMessage {
  type: 'scorm-shim:resize';
  height: number;
}

const isScormResizeMessage = (data: unknown): data is ScormResizeMessage => {
  if (data === null || typeof data !== 'object') return false;
  const payload = data as { type?: unknown; height?: unknown };
  return (
    payload.type === 'scorm-shim:resize' &&
    typeof payload.height === 'number' &&
    Number.isFinite(payload.height) &&
    payload.height > 0
  );
};

// SCORM 1.2 default state — mirrors an empty tracking record. scorm-again
// initializes cmi with these values via loadFromFlattenedJSON before the
// SCORM content in the iframe calls LMSInitialize.
const emptyScormState: Record<string, string> = {
  'cmi.core.lesson_status': 'not attempted',
  'cmi.core.entry': 'ab-initio',
  'cmi.core.lesson_location': '',
  'cmi.core.score.raw': '',
  'cmi.core.total_time': '0000:00:00',
  'cmi.suspend_data': '',
};

// Element-name aliases for common authoring-tool mistakes. SCORM 1.2 puts
// +suspend_data+ at the top of the CMI tree (+cmi.suspend_data+), but many
// hand-authored packages — and Storyline / Captivate — prepend the
// +cmi.core.+ prefix universally and end up calling
// +LMSGetValue('cmi.core.suspend_data')+. Under the +scorm-again+ +
// +CrossFrameAPI+ design, that's a strict miss on both the child cache
// (which we preload with the canonical key) and the parent's data model
// (which rejects the non-standard key). The pre-scorm-again ScormAPI.ts
// on the mega branch had the same alias map; it was retired in PR3 and
// this is the regression that surfaced on staging. We reinstate the map
// both on the parent-side +normalizeCmiElement+ (write path) and by
// mirroring the aliased keys into +initial+ (read path, so the child
// cache-first sync return works).
const CMI_ELEMENT_ALIASES: Record<string, string> = {
  'cmi.core.suspend_data': 'cmi.suspend_data',
  'cmi.suspend': 'cmi.suspend_data',
};

// Read-only learner identity (DVLP-8148): the platform user id, no name or
// email. scorm-again accepts it only before +LMSInitialize+, so it rides in
// +initial+ alongside the tracking state rather than being set later.
const learnerState = (learnerId: Maybe<number>): Record<string, string> =>
  learnerId
    .map<Record<string, string>>((id) => ({ 'cmi.core.student_id': id.toString() }))
    .getOrElseValue({});

const withCmiAliases = (state: Record<string, string>): Record<string, string> => ({
  ...state,
  'cmi.core.suspend_data': state['cmi.suspend_data'] ?? '',
  'cmi.suspend': state['cmi.suspend_data'] ?? '',
});

const segmentResourceFromState = (state: SegmentState): Maybe<SegmentResource> => {
  switch (state.kind) {
    case 'ready':
    case 'processing-request':
    case 'completing':
    case 'advancing':
    case 'loaded':
    case 'previous-advancing':
    case 'advancing-to':
    case 'completing-and-advancing':
    case 'submitting-and-advancing':
    case 'schedule-session':
    case 'quiz-failed':
    case 'reporting-results':
      return just(state.segmentResource);
    default:
      return nothing();
  }
};

const parseCourseRegistrationIdFromUrl = (link: Link): Maybe<number> => {
  try {
    const url = new URL(link.href, window.location.origin);
    return fromNullable(url.searchParams.get('course_registration_id')).map((raw) =>
      parseInt(raw, 10),
    );
  } catch {
    return nothing();
  }
};

// Convert stored seconds back to the SCORM 1.2 total_time string format
// (HHHH:MM:SS.SS). Kept in sync with the parse function on the Ruby side
// (Organizations::ScormTracking#parse_scorm_time).
const formatSecondsToScormTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${hours.toString().padStart(4, '0')}:${minutes.toString().padStart(2, '0')}:${secs
    .toString()
    .padStart(2, '0')}`;
};

interface Props {
  segment: ScormSegmentType;
  segmentStore: SegmentStore;
}

/**
 * Renders a SCORM 1.2 package inside an iframe that loads from the media
 * host (CloudFront). The SCORM content walks +window.parent.API+ per the
 * SCORM spec; because iframe and parent are on different origins that JS
 * access throws cross-origin. To bridge it we:
 *
 * - Run scorm-again's +Scorm12API+ on the parent (this file), wrapped in
 *   +CrossFrameLMS+ which listens for +postMessage+ from the iframe.
 * - The child-side shim (+CrossFrameAPI+) is injected into the SCORM
 *   package's launch HTML at extraction time by
 *   +ProcessScormPackageJob+; it exposes +window.API+ inside the iframe
 *   and proxies every LMS method call to the parent over postMessage.
 *
 * Resume: initial state comes from the +tracking+ prop on the segment,
 * loaded into the API with +loadFromFlattenedJSON+ before we wire the
 * bridge, so the content sees the learner's saved location/status when
 * it calls +LMSGetValue+.
 *
 * Persistence: we intercept +LMSCommit+ via +.on(...)+ and POST the
 * current CMI snapshot to +/learning_platform/scorm_tracking+.
 * scorm-again's built-in +lmsCommitUrl+ is deliberately unused because
 * we need to shape the payload to match our controller's contract
 * (+scorm_package_id+, +course_registration_id+, etc.).
 */
function ScormSegment({ segment, segmentStore }: Props) {
  const { scormPackage, tracking, learnerId } = segment;
  const apiRef = useRef<Maybe<Scorm12API>>(nothing());
  const bridgeRef = useRef<Maybe<CrossFrameLMS>>(nothing());
  const courseRegistrationIdRef = useRef<Maybe<number>>(nothing());
  const pendingCommitRef = useRef<Maybe<Record<string, unknown>>>(nothing());
  // Iframe DOM handle so the resize-message listener can gate on
  // +event.source+, ignoring stale posts from any prior iframe that
  // is still in the process of unmounting (SPA-style navigation to a
  // new SCORM segment, or a very-fast segment change during autocommit).
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  // The iframe is only rendered once the parent-side +Scorm12API+ +
  // +CrossFrameLMS+ bridge is fully wired. Belt-and-braces with the
  // URL-fragment preload below: the fragment populates the child-side
  // shim's cache synchronously so content's first +LMSGetValue+
  // resolves immediately, and +apiReady+ ensures the parent-side store
  // has been seeded before the iframe starts loading — so even if the
  // fragment is dropped (length limit, encoding error), the parent's
  // async responses still arrive against a fully-initialized store.
  const [apiReady, setApiReady] = useState(false);

  // Iframe height reported by the child-side shim via +postMessage+
  // (see +ProcessScormPackageJob.build_shim_script_block+'s height-
  // reporter block, DVLP-7702). +nothing()+ before the first message
  // -> CSS +min-height+ kicks in as the pre-report default so the
  // iframe isn't 0-tall while content mounts. +just(px)+ after a
  // report -> we set the inline height directly and the iframe
  // matches its content. Capped at 90% of the viewport height so
  // pathologically tall content can't push the outer page layout off
  // the screen; if content exceeds the cap the outer page scroll
  // handles it (iframe itself has +overflow: hidden+).
  const [iframeHeightPx, setIframeHeightPx] = useState<Maybe<number>>(nothing());

  // The CMI state we want the SCORM API to see on first LMSGetValue.
  // Used in two places:
  //
  // 1. Parent-side: +api.loadFromFlattenedJSON(initial)+ inside the
  //    setup +useEffect+, so +CrossFrameLMS+ responds with these values
  //    when the child shim's postMessage arrives.
  //
  // 2. Child-side preload: JSON-encoded into the iframe src as a URL
  //    fragment. The injected shim reads +window.location.hash+
  //    synchronously at construction (see
  //    +ProcessScormPackageJob.injected_html+) and populates its
  //    +_cache+ before Storyline / Rise / iSpring content scripts run.
  //    Without this, the child cache is empty on the first
  //    +LMSGetValue+ (postMessage roundtrip hasn't resolved yet),
  //    Storyline sees empty +cmi.suspend_data+, decides "no prior
  //    state," and wipes the learner's saved progress on the next
  //    autocommit.
  //
  // Resume semantics — two corrections vs a naive pass-through:
  //
  // 1. +lesson_status+ round-trips through our Rails enum, which stores
  //    the underscore form (+not_attempted+) while the SCORM 1.2 spec
  //    values use a space (+not attempted+). Seeding the underscore
  //    verbatim gives the content an invalid status; it typically
  //    defaults to a fresh session and never checks suspend_data. Map
  //    the enum key back to the spec form.
  //
  // 2. Storyline / Rise / iSpring packages store all resume state in
  //    +cmi.suspend_data+ as an opaque blob and never explicitly call
  //    +LMSSetValue('cmi.core.lesson_status', 'incomplete')+. So even
  //    with saved state, the learner comes back to a +not attempted+
  //    status and the content starts fresh. When we can see the learner
  //    has meaningful saved state (suspend_data or lesson_location),
  //    force +lesson_status: incomplete+ and set +cmi.core.entry: resume+
  //    so the content picks up where it left off.
  const initial: Record<string, string> = useMemo(
    () =>
      withCmiAliases({
        ...tracking
          .map<Record<string, string>>((t) => {
            const lessonLocation = t.lessonLocation.getOrElseValue('');
            const hasProgress = t.suspendData !== '' || lessonLocation !== '';
            const specStatus =
              t.lessonStatus === 'not_attempted' ? 'not attempted' : t.lessonStatus;
            return {
              'cmi.core.lesson_status': hasProgress ? 'incomplete' : specStatus,
              'cmi.core.entry': hasProgress ? 'resume' : 'ab-initio',
              'cmi.core.lesson_location': lessonLocation,
              'cmi.core.score.raw': t.scoreRaw.map((s) => s.toString()).getOrElseValue(''),
              'cmi.core.total_time': formatSecondsToScormTime(t.totalTime),
              'cmi.suspend_data': t.suspendData,
            };
          })
          .getOrElseValue(emptyScormState),
        ...learnerState(learnerId),
      }),
    [tracking, learnerId],
  );

  // URL fragment carrying the initial CMI state to the injected shim
  // inside the iframe. The fragment is client-side only — browsers
  // strip +#+ from HTTP requests, so no learner state ends up in
  // Rails / CloudFront logs. Kept in sync with +initial+ above.
  //
  // Size ceiling: URL fragments have browser-dependent limits (~64KB in
  // Chrome as of 2026). Typical Storyline / Rise +suspend_data+ is
  // single-digit KB; pathological packages that exceed the limit will
  // fall through the injected shim's +hash.indexOf(prefix) === 0+ check
  // and land in the async cache-warm fallback path.
  //
  // If +JSON.stringify+ or +encodeURIComponent+ throws (or the payload
  // exceeds the browser's URL-fragment length limit and is silently
  // truncated to unparseable), the shim's async cache warm still runs
  // as a fallback — the resume just won't be instant. We log so a
  // regression here is visible in Honeybadger's console-error stream
  // instead of silently degrading every resume.
  const preloadFragment = useMemo((): string => {
    try {
      return `#_scormPreload=${encodeURIComponent(JSON.stringify(initial))}`;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[ScormSegment] preload fragment encode failed:', err);
      return '';
    }
  }, [initial]);

  const getCourseRegistrationId = (): Maybe<number> =>
    courseRegistrationIdRef.current.cata({
      Just: (id) => just(id),
      Nothing: () =>
        segmentResourceFromState(segmentStore.state)
          .andThen((resource) =>
            fromNullable(
              resource.links.find((link) => link.rel === 'progress' && link.method === 'get'),
            ),
          )
          .andThen(parseCourseRegistrationIdFromUrl)
          .map((id) => {
            courseRegistrationIdRef.current = just(id);
            return id;
          }),
    });

  // Refetch the segment resource and swap it in via
  // +refreshSegmentResource+, which only accepts the swap while the segment
  // is sitting in 'ready' / 'processing-request'. Non-destructive there --
  // +SegmentDetail+ renders the same before and after, and the setup
  // +useEffect+ deps are +[segment.id]+ which does not change, so the iframe
  // stays mounted and the bridge is not torn down. Used to flip
  // +payload.completability+ from +not-completable+ to +completable+ the
  // instant a completing SCORM commit lands, so the Next Segment button
  // appears without a page refresh.
  //
  // It called +segmentStore.ready+ directly until BUG-570. The terminal
  // commit that triggers this fires exactly when the learner turns to click
  // Next Segment, so the refetch routinely landed mid-advance -- unmounting
  // the iframe (reloading the package and losing the learner's place) and
  // leaving the advance with a state +advanceTo+ rejects, stranding them on
  // the segment with no error.
  const refetchSegmentResource = (): void => {
    segmentResourceFromState(segmentStore.state)
      .andThen((resource) =>
        fromNullable(resource.links.find((link) => link.rel === 'self' && link.method === 'get')),
      )
      .map((selfLink) => {
        callApi(
          segmentResourceDecoder,
          {},
        )(selfLink).fork(
          (error) => {
            // eslint-disable-next-line no-console
            console.error('[ScormSegment] Failed to refetch segment resource:', error);
          },
          (newResource) => segmentStore.refreshSegmentResource(newResource),
        );
      });
  };

  const postTracking = (cmiData: Record<string, unknown>, onSuccess?: () => void): void => {
    getCourseRegistrationId().cata({
      Nothing: () => {
        pendingCommitRef.current = just(cmiData);
      },
      Just: (courseRegistrationId) => {
        pendingCommitRef.current = nothing();

        const trackingLink: Link = {
          rel: 'scorm-tracking',
          href: '/learning_platform/scorm_tracking',
          method: 'post',
          type: 'application/json',
        };

        const payload = {
          scorm_tracking: {
            scorm_package_id: scormPackage.id,
            segment_id: segment.id,
            course_id: segment.programId,
            course_module_id: segment.moduleId,
            course_registration_id: courseRegistrationId,
            registration_type: segment.registrationType,
            cmi_data: cmiData,
          },
        };

        postToApi(payload)(trackingLink).fork(
          (error) => {
            // eslint-disable-next-line no-console
            console.error('[ScormSegment] Failed to save tracking data:', error);
          },
          () => {
            onSuccess?.();
          },
        );
      },
    });
  };

  // Flush any pending commit once the segment resource resolves and we
  // can parse course_registration_id out of the progress link.
  useEffect(() => {
    getCourseRegistrationId().map(() => {
      pendingCommitRef.current.map((data) => postTracking(data));
    });
  }, [segmentStore.state.kind]);

  useEffect(() => {
    const api = new Scorm12API({
      autocommit: true,
      autocommitSeconds: 10,
      lmsCommitUrl: false, // we do our own POST — see postTracking above
      logLevel: 4, // 4 = warnings and errors only
    });

    // Storyline and some Rise / iSpring packages call LMS methods with
    // a duplicated +cmi.core.+ prefix — e.g. +cmi.core.cmi.core.lesson_status+
    // instead of +cmi.core.lesson_status+. scorm-again correctly rejects
    // these as invalid data-model elements (SCORM error 401), which the
    // content interprets as "no LMS state available" and starts fresh —
    // resume fails silently. Strip the duplicated prefix before the call
    // reaches the store. The old +ScormAPI.ts+ handled this via
    // +normalizeElementName+; PR3 lost that when we adopted scorm-again.
    //
    // We overwrite BOTH +lmsGetValue+/+lmsSetValue+ (lowercase, used by
    // internal paths) AND +LMSGetValue+/+LMSSetValue+ (uppercase, aliased
    // by the +Scorm12API+ constructor to the lowercase methods at
    // construction time and captured by reference — a plain lowercase
    // reassignment does not propagate). The +CrossFrameLMS+ postMessage
    // bridge dispatches on the uppercase names it receives from the
    // child +CrossFrameAPI+ proxy (+api[msg.method]+ where +msg.method+ is
    // +'LMSGetValue'+ etc.), so wrapping only the lowercase would leave
    // the actual iframe call path unnormalized.
    // Two normalizations run in order:
    // 1. Strip a duplicated +cmi.core.+ prefix (Storyline / Rise / iSpring
    //    bug where content emits +cmi.core.cmi.core.lesson_status+ etc).
    // 2. Alias non-standard element names — mainly +cmi.core.suspend_data+
    //    and +cmi.suspend+, both common authoring-tool mistakes for the
    //    top-level +cmi.suspend_data+ (see +CMI_ELEMENT_ALIASES+ above
    //    for the rationale + PR3-regression history).
    const normalizeCmiElement = (element: string): string => {
      const doublePrefixFixed = element.startsWith('cmi.core.cmi.core.')
        ? element.replace('cmi.core.cmi.core.', 'cmi.core.')
        : element;
      return CMI_ELEMENT_ALIASES[doublePrefixFixed] ?? doublePrefixFixed;
    };
    const upstreamGet = api.lmsGetValue.bind(api);
    const upstreamSet = api.lmsSetValue.bind(api);
    const wrappedGet = (element: string) => upstreamGet(normalizeCmiElement(element));

    // DVLP-7757 auto-scroll: content emits +LMSSetValue('cmi.core.lesson_location', <id>)+
    // on each scene advance (SCORM 1.2 spec's bookmarking field). When the
    // value transitions to a new non-empty scene id AND the learner is
    // scrolled past the iframe's top (+rect.top < 0+), scroll the outer
    // page so the iframe top aligns with the viewport top. Replaces the
    // DVLP-7715 height-delta heuristic, which missed on small-scene
    // packages (objective-first-spike on staging). Seeded from +initial+
    // so a resume from a saved location doesn't false-fire on the first
    // write. Contract: content authors must emit +lesson_location+ on
    // every scene advance; packages that don't emit it get no auto-scroll.
    let lastLessonLocation = initial['cmi.core.lesson_location'] ?? '';
    const wrappedSet = (element: string, value: string) => {
      const normalized = normalizeCmiElement(element);
      const result = upstreamSet(normalized, value);
      if (
        normalized === 'cmi.core.lesson_location' &&
        value !== '' &&
        value !== lastLessonLocation
      ) {
        lastLessonLocation = value;
        const iframe = iframeRef.current;
        if (iframe !== null && iframe.getBoundingClientRect().top < 0) {
          iframe.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
      return result;
    };
    api.lmsGetValue = wrappedGet;
    api.lmsSetValue = wrappedSet;
    api.LMSGetValue = wrappedGet;
    api.LMSSetValue = wrappedSet;

    // Seed the CMI state so LMSGetValue calls from the content resolve
    // to the learner's saved position/status.
    //
    // NOTE: this is only one half of the resume seed. The parent-side
    // store gets the values here via +loadFromFlattenedJSON+, but the
    // child-side +CrossFrameAPI+ shim inside the iframe maintains its
    // own local cache that it warms asynchronously via postMessage.
    // Storyline calls +LMSGetValue('cmi.suspend_data')+ synchronously
    // on init — before the postMessage roundtrip resolves — and would
    // see empty. We fix that by encoding the same +initial+ state as a
    // URL fragment on the iframe src (see +preloadFragment+ above);
    // the injected shim reads the fragment synchronously and populates
    // its cache before content scripts run. See +initial+'s comment for
    // the resume-status heuristic (enum-form normalization + forcing
    // +incomplete+/+resume+ when the learner has saved state).
    api.loadFromFlattenedJSON(initial);

    // Intercept LMSCommit — our own POST with the payload shape our
    // controller expects. The event fires after scorm-again has already
    // reconciled the CMI store, so getFlattenedCMI() is up to date.
    //
    // When the commit that just landed reports a terminal SCORM 1.2
    // status (+completed+ or +passed+), refetch the segment resource on
    // POST success. +completable?+ on the backend is derived from the
    // tracking record, so the new resource flips +completability+ to
    // +completable+ and the Next Segment button appears immediately —
    // no page refresh required.
    api.on('LMSCommit', () => {
      const cmi = api.getFlattenedCMI() as Record<string, unknown>;
      const status = cmi['cmi.core.lesson_status'];
      const isTerminal = status === 'completed' || status === 'passed';
      postTracking(cmi, isTerminal ? refetchSegmentResource : undefined);
    });

    // Wrap the API with the postMessage bridge. targetOrigin '*' matches
    // both same-origin (local :file storage) and the media CDN host in
    // deployed envs; the actual authorization is on our tracking POST,
    // not on postMessage receipt.
    const bridge = new CrossFrameLMS(api, '*');

    apiRef.current = just(api);
    bridgeRef.current = just(bridge);
    // Only flip +apiReady+ once the seed has been applied AND the
    // postMessage listener is wired. This is the signal that gates the
    // iframe render — see the +useState+ declaration at the top of the
    // component for the full explanation.
    setApiReady(true);

    return () => {
      bridgeRef.current.map((b) => b.destroy());
      apiRef.current = nothing();
      bridgeRef.current = nothing();
      setApiReady(false);
      setIframeHeightPx(nothing());
    };
    // We want a single API instance for the lifetime of the segment;
    // remount only when the actual segment changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segment.id]);

  // Listen for the child-side shim's +scorm-shim:resize+ postMessage
  // (see the height-reporter block in +ProcessScormPackageJob+). We
  // deliberately do NOT gate on +event.origin+ because scorm-again's
  // +CrossFrameLMS+ already uses +targetOrigin: '*'+ for the same
  // reason (media host isn't fixed across envs / can be
  // +file://null+ in local storage-mode). Trust boundary is the
  // combination of +isScormResizeMessage+ (payload shape check) and
  // the +event.source+ === +iframeRef.current.contentWindow+ filter
  // below (defense against any prior segment's iframe posting a
  // final message while its DOM is tearing down).
  //
  // Height is floored at +MIN_IFRAME_HEIGHT_PX+ (mirrors the CSS
  // +min-height+) so a tiny +scrollHeight+ report can't collapse the
  // iframe once the inline height takes over. No ceiling -- see
  // +MIN_IFRAME_HEIGHT_PX+'s comment for why capping would defeat
  // the whole ticket. Iframe grows to whatever the content reports;
  // overflow is the outer page's job.
  //
  // Auto-scroll on scene advance is handled by the +wrappedSet+
  // interceptor in the setup +useEffect+ above, keyed off content's
  // +LMSSetValue('cmi.core.lesson_location', ...)+ writes (DVLP-7757).
  // The previous height-delta heuristic (DVLP-7715) was removed --
  // small-scene packages produced deltas below its 200 px threshold
  // and never scrolled.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (!isScormResizeMessage(event.data)) return;
      const currentIframe = iframeRef.current;
      if (currentIframe !== null && event.source !== currentIframe.contentWindow) return;
      const bounded = Math.max(MIN_IFRAME_HEIGHT_PX, Math.ceil(event.data.height));
      setIframeHeightPx(just(bounded));
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [segment.id]);

  const postVisibleHeight = (): void => {
    fromNullable(iframeRef.current)
      .andThen((iframe) => fromNullable(iframe.contentWindow))
      .do((child) => {
        child.postMessage({ type: 'scorm-host:viewport', height: visibleSegmentHeight() }, '*');
      });
  };

  useEffect(() => {
    let frame = 0;
    const onResize = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(postVisibleHeight);
    };
    window.addEventListener('resize', onResize);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', onResize);
    };
  }, [segment.id]);

  return scormPackage.launchUrl
    .map((launchUrl) => (
      <div
        className={style.scormContainer}
        data-test-scorm-segment={segment.id}
        data-intercom-target="scorm-segment-container"
      >
        {apiReady && (
          <iframe
            ref={iframeRef}
            src={`${launchUrl}${preloadFragment}`}
            className={style.scormFrame}
            title={segment.title}
            sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals"
            // Delegates to embedded players (e.g. Mursion); the browser still prompts the learner.
            allow="autoplay; camera; microphone; fullscreen; display-capture; clipboard-write"
            allowFullScreen
            onLoad={postVisibleHeight}
            // +scrolling="no"+ is deprecated in HTML5 but universally
            // honored -- WebKit, Blink, and Firefox all treat it as
            // +overflow: hidden+ on the iframe's own document, which
            // suppresses the ornamental scrollbar the browser would
            // otherwise reserve even when content fits (visible on
            // smaller viewports where the iframe document's default
            // +overflow: auto+ reserves scrollbar-track space regardless
            // of whether there's anything to scroll). We size the iframe
            // to full content height via +iframeHeightPx+, so the
            // iframe never needs its own scroll -- the outer platform
            // page handles all overflow.
            scrolling="no"
            data-intercom-target="scorm-content-iframe"
            style={iframeHeightPx.cata<CSSProperties>({
              Just: (h) => ({ height: `${h}px` }),
              Nothing: () => ({}),
            })}
          />
        )}
      </div>
    ))
    .getOrElse(() => (
      <div className={style.scormContainer} data-intercom-target="scorm-processing-notice">
        <div className={style.processingMessage}>
          <T kind="SCORM package is being processed. Please check back shortly." />
        </div>
      </div>
    ));
}

export default observer(ScormSegment);
